/**
 * 「{paymentNotice}作成」/payment-notices/new（#233 3b、docs/93 §15.6 の入口2＝{order}なし）：{vendor}と{project}（任意）だけ聞く。?group=・?partner= で始められる。
 * 承諾済の{order}から（紙の画面の「⋯」→「{paymentNotice}作成」）はここを通らない。PC だけ（PC_ONLY_FEATURES）
 */
import { PAYMENT_NOTICE_MSG, PURCHASE_MSG, type GroupList, type Partner, type PaymentNotice } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { ErrorText, Field } from "../../components/ui.jsx";
import { PartnerField } from "../../components/PartnerPickerDrawer.jsx";
import { t, useLabels } from "../../labels.js";

export function PaymentNoticeNewPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [vendorId, setVendorId] = useState(sp.get("partner") ?? "");
  const [groupId, setGroupId] = useState(sp.get("group") ?? "");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  usePageName(t(PAYMENT_NOTICE_MSG.newTitle));
  const back = useBackNav(groupId && sp.get("group") ? { to: `/groups/${groupId}?tab=purchases`, label: L.project } : { to: "/payment-notices", label: t(PAYMENT_NOTICE_MSG.list) }, !!vendorId);
  /** 選んだ{vendor}（#289：取引先検索のドロアー＝#288 の部品。?partner= で入ったときは名前を引く） */
  const [picked, setPicked] = useState<{ name: string; branch: string | null; contact: string | null } | null>(null);
  const entry = useQuery({ queryKey: ["partner", sp.get("partner")], queryFn: () => api<Partner>("GET", `/partners/${sp.get("partner")}`), enabled: !!sp.get("partner") });
  const shown = picked ?? (entry.data && entry.data.id === vendorId ? { name: entry.data.displayName, branch: null, contact: null } : null);
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all") });
  const create = async () => {
    if (!vendorId) {
      setErr(t(PAYMENT_NOTICE_MSG.vendorRequired));
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const n = await api<PaymentNotice>("POST", "/payment-notices", { vendorId, projectGroupId: groupId || null, ...(subject.trim() ? { subject: subject.trim() } : {}) });
      void qc.invalidateQueries({ queryKey: ["payment-notices"] });
      nav(`/payment-notices/${n.id}`, { replace: true });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{t(PAYMENT_NOTICE_MSG.newTitle)}</h1>
      </div>
      <div className="paper" style={{ maxWidth: 640, padding: 20 }} data-testid="notice-new-form">
        <div style={{ marginBottom: 14 }}>
          <PartnerField
            role="vendor"
            label={L.vendor}
            value={shown}
            onPick={(c) => {
              setVendorId(c.id);
              setPicked({ name: c.displayName, branch: null, contact: null });
              setErr(undefined);
            }}
            testId="notice-new-vendor"
          />
        </div>
        <Field id="pnn_group" label={`${L.project}（任意）`} style={{ marginBottom: 14 }}>
          <select id="pnn_group" className="inp" value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="notice-new-group">
            <option value="">—</option>
            {(groups.data?.items ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="pnn_subject" label={`${PAYMENT_NOTICE_MSG.colSubject}（任意）`} style={{ marginBottom: 14 }}>
          <input id="pnn_subject" className="inp" placeholder=" " maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="notice-new-subject" />
        </Field>
        <ErrorText>{err}</ErrorText>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn primary" disabled={busy || !vendorId} onClick={() => void create()} data-testid="notice-new-create">
            {t(PAYMENT_NOTICE_MSG.create)}
          </button>
        </div>
      </div>
    </>
  );
}
